/**
 * Handling of the `return` query parameter.
 *
 * When someone opens a private URL without an active session they are sent to
 * the login page with `?return=<full url they asked for>`. Once authenticated
 * they are taken to that URL instead of the default portal page.
 *
 * Only URLs we explicitly allow are honored, so the parameter cannot be used to
 * bounce a freshly authenticated user to an arbitrary site. On a deployed build
 * that means this app's own origin and any https page on OWN_DOMAIN, which every
 * app in the suite is served from — add entries to EXTRA_ALLOWED_RETURN_ORIGINS
 * to opt a project hosted elsewhere in.
 */

import { isDevelopment } from 'utils/environment';

export const RETURN_PARAM = 'return';

export const LOGIN_PATH = '/';

export const ADMIN_PORTAL_PATH = '/app/video-console';

export const PORTAL_PATH = '/app/stations-monitoring';

// The domain every app in the suite is served from, the apex or a subdomain such
// as accounts.sweat440.dev. Any https page under it is an allowed return target,
// so a sibling app handing a user here needs no entry of its own — this is the
// same rule the accounts app applies to us.
export const OWN_DOMAIN = 'sweat440.dev';

// Extension point: origins outside OWN_DOMAIN that are allowed as return
// targets, e.g. 'https://reports.sweat440.com'.
export const EXTRA_ALLOWED_RETURN_ORIGINS = [];

const LOCALHOST_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]'];

const currentOrigin = () => window.location.origin;

/**
 * Whether the URL is a page on OWN_DOMAIN. The hostname comes back from the URL
 * parser already lowercased, and the leading dot is what keeps
 * `notsweat440.dev` and `sweat440.dev.example.com` out.
 *
 * HTTP is deliberately not matched: an app of ours is served over https, and
 * plain HTTP under the domain is more likely a host someone else has taken over
 * than one of our own.
 */
const isOwnDomain = (url) =>
  url.protocol === 'https:' &&
  (url.hostname === OWN_DOMAIN || url.hostname.endsWith(`.${OWN_DOMAIN}`));

/**
 * Whether we are allowed to send a user to this URL.
 *
 * Locally each app in the suite runs on its own port, so ports cannot be
 * pinned — any localhost URL is accepted. Deployed builds never take this
 * branch, so a production `return` still has to name an allowed origin.
 */
export const isAllowedReturnUrl = (url) =>
  url.origin === currentOrigin() ||
  isOwnDomain(url) ||
  EXTRA_ALLOWED_RETURN_ORIGINS.includes(url.origin) ||
  (isDevelopment() && LOCALHOST_HOSTNAMES.includes(url.hostname));

/**
 * Parses a `return` value into a URL, or returns null when it is missing,
 * unparseable, or points somewhere we are not allowed to send the user.
 */
const parseReturnUrl = (returnUrl) => {
  if (!returnUrl) {
    return null;
  }

  let parsed;
  try {
    parsed = new URL(returnUrl, currentOrigin());
  } catch (error) {
    return null;
  }

  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    return null;
  }
  if (!isAllowedReturnUrl(parsed)) {
    return null;
  }
  // Returning to the login page would bounce the user straight back here.
  if (parsed.origin === currentOrigin() && parsed.pathname === LOGIN_PATH) {
    return null;
  }

  return parsed;
};

/** Builds the absolute URL for a router location. */
export const buildReturnUrl = ({
  pathname = LOGIN_PATH,
  search = '',
  hash = '',
} = {}) => `${currentOrigin()}${pathname}${search}${hash}`;

/** The login location to redirect to, carrying `from` as the return target. */
export const buildLoginLocation = (from) => {
  const params = new URLSearchParams();
  params.set(RETURN_PARAM, buildReturnUrl(from));

  return { pathname: LOGIN_PATH, search: `?${params.toString()}` };
};

export const readReturnUrl = (search) =>
  new URLSearchParams(search || '').get(RETURN_PARAM);

/** The page a user lands on when there is no return target to honor. */
export const getDefaultPortalPath = (me) =>
  me?.role === 'admin' ? ADMIN_PORTAL_PATH : PORTAL_PATH;

/**
 * Resolves where to send an authenticated user, given the login page's query
 * string. Falls back to `fallbackPath` when there is no usable return target.
 *
 * Returns either `{ external: false, location }` for an in-app redirect or
 * `{ external: true, url }` for a full page navigation.
 */
export const resolveReturnTarget = (search, fallbackPath) => {
  const parsed = parseReturnUrl(readReturnUrl(search));

  if (!parsed) {
    return {
      external: false,
      location: { pathname: fallbackPath, search: '', hash: '' },
    };
  }

  if (parsed.origin !== currentOrigin()) {
    return { external: true, url: parsed.toString() };
  }

  return {
    external: false,
    location: {
      pathname: parsed.pathname,
      search: parsed.search,
      hash: parsed.hash,
    },
  };
};
